import { z } from 'zod';
import { commaSeparated } from '../common/comma-separated';
import { ListSearchTermSchema } from '../common/search-term.schema';
import { LEGAL_BASES, LegalBasisSchema, type LegalBasis } from '../common/legal-basis.schema';
import { LeadLinksSchema } from '../common/links.schemas';
import {
  PropertyCategorySchema,
  PropertySubtypeSchema,
  isValidCategorySubtype,
  type PropertyCategory,
  type PropertySubtype,
} from '../common/property-taxonomy.schema';
import { DEAL_LOSS_REASONS, DealLossReasonSchema, type DealLossReason, type DealPhase } from '../deal/deals.schemas';
import { LEAD_SOURCES, LeadSourceSchema, type LeadSource } from '../common/lead-source.schema';
import { MANUAL_COMMUNICATION_TYPES, COMMUNICATION_OUTCOME_TOKENS } from '../common/communication-vocabulary.schema';
import { TransactionTypeSchema } from '../common/transaction-type.schema';
import { refersTo } from '../common/refers-to';

const WorkspaceIdPathParamsSchema = z.object({ workspaceId: z.uuid() });

// Re-exported from `common/` (IMM-456.6) so the Deal's `origin` can reference the
// same set without a `lead` ↔ `deal` module cycle. Every existing import of
// `LEAD_SOURCES` / `LeadSourceSchema` / `LeadSource` from here keeps working.
export { LEAD_SOURCES, LeadSourceSchema, type LeadSource };

// Lifecycle. `NEW` is the only status reachable via capture (IMM-190);
// the transitions land in slices 4 (IMM-192) and 5 (IMM-193).
export const LEAD_STATUSES = ['NEW', 'CONTACTED', 'CONVERTED', 'LOST', 'ARCHIVED'] as const;
export const LeadStatusSchema = z.enum(LEAD_STATUSES);
export type LeadStatus = z.infer<typeof LeadStatusSchema>;

// Why a lead was marked lost. The lead shares the deal's Verlustgrund set
// (IMM-429) so lead and deal offer the same granular reasons; the one copy that
// must move in lockstep by hand is the `lead_status_changes_reason_check` DB
// constraint (lead module schema + migration). Pre-IMM-429 records (5
// lead-specific values) were remapped onto this set by the lead module's 0003
// migration. English literals on the wire; German labels render via @immo/i18n.
// Required on mark-lost (IMM-192).
export const LEAD_LOSS_REASONS = DEAL_LOSS_REASONS;
export const LeadLossReasonSchema = DealLossReasonSchema;
export type LeadLossReason = DealLossReason;

export const LEAD_LOSS_NOTE_MAX_LENGTH = 500;

export const LEAD_SALUTATIONS = ['MRS', 'MR', 'DIVERSE', 'FAMILY', 'OTHER'] as const;
export const LeadSalutationSchema = z.enum(LEAD_SALUTATIONS);
export type LeadSalutation = z.infer<typeof LeadSalutationSchema>;

// The lead's Immobilientyp is the shared two-level taxonomy (IMM-432.2): a
// category plus an optional subtype. Both stay optional on a body — a lead is
// first-touch data. A subtype is only valid alongside its owning category, so
// the create/edit bodies pair-check it via `superRefine` below.
function refineImmobilientyp(
  value: { readonly category?: PropertyCategory | undefined; readonly subtype?: PropertySubtype | undefined },
  ctx: z.RefinementCtx,
): void {
  if (value.subtype === undefined) return;

  if (value.category === undefined || !isValidCategorySubtype(value.category, value.subtype)) {
    ctx.addIssue({
      code: 'custom',
      path: ['subtype'],
      message: `Subtype ${value.subtype} does not belong to category ${value.category ?? 'null'}`,
    });
  }
}

// ---------------------------------------------------------------------------
// Deprecated `objectType` compatibility shim (public POST /leads only)
// ---------------------------------------------------------------------------
//
// IMM-432.2 replaced the lead's own 7-value object-type enum with the shared
// `category` + `subtype` taxonomy. Because `CreateLeadPropertySchema` is
// `.strict()`, that silently turned every partner push still sending
// `objectType` into a hard 400 on an unrecognised key — the public lead-push
// API broke for integrations that had no way to know. This shim accepts the old
// field again and maps it forward, so a partner keeps working until they
// migrate.
//
// TEMPORARY. Drop the field, this map, `resolveLeadImmobilientyp` and their
// tests once every partner integration posts `category`/`subtype`. Removing it
// is itself a breaking change to a `docs: 'PUBLIC'` endpoint, so it goes
// through the same announce-then-remove path — see the public API snapshot
// guard in `apps/api-docs/src/openapi.test.ts`.
export const LEGACY_LEAD_OBJECT_TYPES = [
  'APARTMENT',
  'HOUSE',
  'PLOT',
  'MULTI_FAMILY',
  'COMMERCIAL',
  'GARAGE',
  'OTHER',
] as const;
export const LegacyLeadObjectTypeSchema = z.enum(LEGACY_LEAD_OBJECT_TYPES);
export type LegacyLeadObjectType = z.infer<typeof LegacyLeadObjectTypeSchema>;

// The forward mapping, identical to the in-place backfill the lead module's
// 0002 migration ran over `lead_property.object_type` — a lead pushed with a
// legacy token today lands on exactly the taxonomy a lead pushed with it before
// IMM-432.2 was migrated onto. `OTHER` has no token in the taxonomy and
// migrated to NULL, so it resolves to no category (the lead is still created).
export const LEGACY_OBJECT_TYPE_TO_IMMOBILIENTYP: Record<
  LegacyLeadObjectType,
  { readonly category: PropertyCategory | undefined; readonly subtype: PropertySubtype | undefined }
> = {
  APARTMENT: { category: 'APARTMENT', subtype: undefined },
  HOUSE: { category: 'HOUSE', subtype: undefined },
  PLOT: { category: 'LAND', subtype: undefined },
  MULTI_FAMILY: { category: 'HOUSE', subtype: 'MULTI_FAMILY_HOUSE' },
  COMMERCIAL: { category: 'COMMERCIAL', subtype: undefined },
  GARAGE: { category: 'GARAGE_PARKING', subtype: undefined },
  OTHER: { category: undefined, subtype: undefined },
};

/**
 * Resolves the Immobilientyp a create-lead property section describes,
 * collapsing the deprecated `objectType` onto the current taxonomy. Callers
 * (the create route) use this instead of reading `category`/`subtype` directly,
 * so the compatibility shim lives in one place and disappears in one edit.
 *
 * `category` always wins when present — the schema rejects a payload carrying
 * both, so a resolved `objectType` can never contradict an explicit category.
 */
export function resolveLeadImmobilientyp(property: {
  readonly category?: PropertyCategory | undefined;
  readonly subtype?: PropertySubtype | undefined;
  readonly objectType?: LegacyLeadObjectType | undefined;
}): { readonly category: PropertyCategory | null; readonly subtype: PropertySubtype | null } {
  if (property.category !== undefined || property.objectType === undefined) {
    return { category: property.category ?? null, subtype: property.subtype ?? null };
  }

  const mapped = LEGACY_OBJECT_TYPE_TO_IMMOBILIENTYP[property.objectType];

  return { category: mapped.category ?? null, subtype: mapped.subtype ?? null };
}

// Create-body variant of the pairing check: the same subtype rule, plus a
// rejection of the two vocabularies at once. Sending both is a confused client,
// and guessing which one it means would silently mis-record the lead.
function refineCreateImmobilientyp(
  value: {
    readonly category?: PropertyCategory | undefined;
    readonly subtype?: PropertySubtype | undefined;
    readonly objectType?: LegacyLeadObjectType | undefined;
  },
  ctx: z.RefinementCtx,
): void {
  if (value.objectType !== undefined && value.category !== undefined) {
    ctx.addIssue({
      code: 'custom',
      path: ['objectType'],
      message: 'objectType is deprecated and cannot be combined with category — send category (+ subtype) only',
    });

    return;
  }

  refineImmobilientyp(value, ctx);
}

// GDPR processing basis per IMM-7; defaults to PRE_CONTRACTUAL_MEASURES.
// The enum lives in `common/legal-basis.schema` because the contact a
// qualified lead converts into (IMM-193) mirrors the same basis.
export { LEGAL_BASES, LegalBasisSchema, type LegalBasis };

const RECEIVED_AT_MAX_AGE_DAYS = 90;

function receivedAtWithinBounds(value: string): boolean {
  const received = new Date(value).getTime();
  const now = Date.now();
  const earliest = now - RECEIVED_AT_MAX_AGE_DAYS * 24 * 60 * 60 * 1000;

  return received <= now && received >= earliest;
}

// Contexted sections (IMM-215). The lead is no longer flat: owner identity +
// contact channels live under `owner` (mandatory — a lead always has a name),
// the object location + characteristics under `property` (optional). Mirrors
// the property module's section model and the 3-step capture wizard.
export const CreateLeadOwnerSchema = z
  .object({
    salutation: LeadSalutationSchema.optional(),
    firstName: z.string().trim().max(80).optional(),
    lastName: z.string().trim().min(1).max(80),
    email: z.email().optional(),
    phone: z.string().trim().min(1).optional(),
    mobile: z.string().trim().min(1).optional(),
  })
  .strict();
export type CreateLeadOwner = z.infer<typeof CreateLeadOwnerSchema>;

export const CreateLeadPropertySchema = z
  .object({
    street: z.string().trim().max(120).optional(),
    houseNumber: z.string().trim().max(10).optional(),
    postalCode: z.string().trim().max(20).optional(),
    city: z.string().trim().max(80).optional(),
    country: z.string().trim().max(80).default('Deutschland'),
    cadastralDistrict: z.string().trim().max(80).optional(),
    cadastralParcel: z.string().trim().max(40).optional(),
    latitude: z.number().min(-90).max(90).optional(),
    longitude: z.number().min(-180).max(180).optional(),
    category: PropertyCategorySchema.optional(),
    subtype: PropertySubtypeSchema.optional(),
    // Deprecated — see the compatibility shim above. Accepted so integrations
    // written against the pre-IMM-432.2 contract keep working; resolved onto
    // `category`/`subtype` by `resolveLeadImmobilientyp`.
    objectType: LegacyLeadObjectTypeSchema.optional().meta({
      deprecated: true,
      description:
        'Deprecated — use `category` (and optionally `subtype`) instead. Still accepted and mapped forward (PLOT → LAND, MULTI_FAMILY → HOUSE + MULTI_FAMILY_HOUSE, GARAGE → GARAGE_PARKING, OTHER → no category). Cannot be combined with `category`. Support will be withdrawn once partner integrations have migrated.',
    }),
    constructionYear: z.number().int().min(1700).max(2100).optional(),
    livingArea: z.number().min(0).optional(),
    plotArea: z.number().min(0).optional(),
    estimatedValue: z.number().min(0).optional(),
  })
  .strict()
  .superRefine(refineCreateImmobilientyp);
export type CreateLeadProperty = z.infer<typeof CreateLeadPropertySchema>;

export const CreateLeadBodySchema = z
  .object({
    owner: CreateLeadOwnerSchema,
    property: CreateLeadPropertySchema.optional(),
    source: LeadSourceSchema,
    leadPartnerId: z.uuid().nullable().default(null),
    organisationId: z.uuid().optional(),
    // Partner-provided opaque identifier (IMM-26). Doubles as the key-scoped
    // idempotency key: replaying the same (api key, partnerLeadId) returns the
    // already-created lead instead of inserting a second one. Carried on the
    // lead for traceability. Ignored for member (JWT) callers — only an
    // API-key push binds it.
    partnerLeadId: z.string().trim().min(1).max(128).optional(),
    notes: z.string().trim().max(5000).optional(),
    legalBasis: LegalBasisSchema.default('PRE_CONTRACTUAL_MEASURES'),
    receivedAt: z.iso
      .datetime()
      .refine(receivedAtWithinBounds, {
        message: 'receivedAt must be between 90 days ago and now',
      })
      .optional(),
    // The owner's Absicht (IMM-792.1) — SALE or RENTAL. Absent means nobody has
    // asked yet; never inferred from source, partner or object type.
    intent: TransactionTypeSchema.optional(),
  })
  .strict();

export type CreateLeadBody = z.infer<typeof CreateLeadBodySchema>;

// Fields every lead representation carries — the inbox row and the full
// LeadResponse both build on this base.
const LeadRowSchema = z.object({
  id: z.uuid(),
  displayName: z.string(),
  address: z.string().nullable(),
  // The Standort (organisation) the lead is currently assigned to; null when
  // unassigned. Only the id is carried — the client maps it to a name via the
  // workspace org tree it already holds.
  organisationId: refersTo('organization', z.uuid()).nullable(),
  source: LeadSourceSchema,
  leadPartnerId: refersTo('lead-partner', z.uuid()).nullable(),
  // The attributed partner's display name, resolved server-side. Partner
  // attribution is workspace-wide (IMM-2.9) — a lead may be attributed to a
  // partner in another organisation than the one it was posted to — so the name
  // is hydrated workspace-wide here rather than from the org-scoped partner list.
  // Null when the lead has no partner (or the partner no longer resolves).
  leadPartnerName: z.string().nullable(),
  // Traceability for an API-pushed lead (IMM-26): the issuing key and the
  // partner-provided id. Both null for manually captured leads. The detail
  // page resolves the key's display name / revoked state from the api-keys
  // endpoint — the lead only carries the id (no cross-module name snapshot).
  apiKeyId: refersTo('api-key', z.uuid()).nullable(),
  partnerLeadId: z.string().nullable(),
  status: LeadStatusSchema,
  createdAt: z.iso.datetime(),
  receivedAt: z.iso.datetime(),
  // The owner's Absicht (IMM-792.1) — SALE or RENTAL. Null means "not yet
  // asked", never a third value and never rendered as a choice.
  intent: TransactionTypeSchema.nullable(),
});

// The inbox row: the base plus two derived columns (IMM-419) that exist ONLY on
// the list read — they deliberately do not ride on LeadResponse, so no endpoint
// advertises a field it doesn't populate. `estimatedValue` surfaces
// `property.estimatedValue` flat for the „Immobilienwert" column; the full
// response carries it inside `property`. `lastContactAt` is max `occurred_at`
// over the lead's own communications (`lead_communications`) — the Lead
// analogue of the Contact derivation (IMM-240), communication-only by
// construction (notes and lifecycle events live elsewhere) — and is resolved by
// the list query alone.
export const LeadListItemSchema = LeadRowSchema.extend({
  estimatedValue: z.number().nullable(),
  lastContactAt: z.iso.datetime().nullable(),
  // The lead's live responsible-member id set (IMM-1111) — the „Zugewiesen"
  // column. Member ids in assignment order (earliest first), `[]` when nobody is
  // responsible. A slim id list like the deal row's `responsibleMemberIds`: the
  // list hydrates names + avatars from the member roster; the single-lead read
  // carries `responsibleMembers` with names instead. List-only, like the two
  // derived columns above — never on `LeadResponse`.
  responsibleMemberIds: z.array(refersTo('member', z.uuid())),
});

export type LeadListItem = z.infer<typeof LeadListItemSchema>;

// Owner section on the wire — identity + contact channels. Always present.
export const LeadOwnerSchema = z.object({
  salutation: LeadSalutationSchema.nullable(),
  firstName: z.string().nullable(),
  lastName: z.string(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  mobile: z.string().nullable(),
});
export type LeadOwner = z.infer<typeof LeadOwnerSchema>;

// Property section on the wire — location + object characteristics. Null when
// the lead carries no property context at all.
export const LeadPropertySchema = z.object({
  street: z.string().nullable(),
  houseNumber: z.string().nullable(),
  postalCode: z.string().nullable(),
  city: z.string().nullable(),
  country: z.string(),
  cadastralDistrict: z.string().nullable(),
  cadastralParcel: z.string().nullable(),
  latitude: z.number().nullable(),
  longitude: z.number().nullable(),
  category: PropertyCategorySchema.nullable(),
  subtype: PropertySubtypeSchema.nullable(),
  constructionYear: z.number().int().nullable(),
  livingArea: z.number().nullable(),
  plotArea: z.number().nullable(),
  estimatedValue: z.number().nullable(),
});
export type LeadProperty = z.infer<typeof LeadPropertySchema>;

export const LeadResponseSchema = LeadRowSchema.extend({
  version: z.number().int().positive(),
  legalBasis: LegalBasisSchema,
  notes: z.string().nullable(),
  createdByMemberId: refersTo('member', z.uuid()),
  // Lifecycle transition state (IMM-192). `lossReason`/`lossNote` are retained
  // on a reopened lead as history (no longer determinative). Stamps record when
  // the lead entered the corresponding terminal-ish state; null while unset.
  lossReason: LeadLossReasonSchema.nullable(),
  lossNote: z.string().nullable(),
  lostAt: z.iso.datetime().nullable(),
  archivedAt: z.iso.datetime().nullable(),
  // ISO instant the lead entered its CURRENT status (IMM-416.2) — the detail
  // header's „seit {Datum}". Always resolvable: the entering transition row, or
  // `receivedAt` for a NEW lead that has no transition row. Never null.
  statusSince: z.iso.datetime(),
  // Conversion result (IMM-213 Qualify). All four are null until the lead is
  // qualified; once CONVERTED they carry the ids of the Contact, Property and
  // DRAFT Deal created in the same atomic transaction (the detail-page chips)
  // and the instant the conversion landed.
  convertedContactId: refersTo('contact', z.uuid()).nullable(),
  convertedPropertyId: refersTo('property', z.uuid()).nullable(),
  convertedDealId: refersTo('deal', z.uuid()).nullable(),
  convertedAt: z.iso.datetime().nullable(),
  // Who qualified the lead (IMM-416.5), for the converted-lead confirmation
  // panel's „Von {Name} am {Datum} qualifiziert." line. The id is the actor on
  // the CONVERTED status-change row (already persisted); the display name is
  // resolved on the detail read only. A `null` name (deactivated / non-member
  // actor, or a mutation response) makes the FE render the anonymous fallback.
  qualifiedByMemberId: refersTo('member', z.uuid()).nullable(),
  qualifiedByDisplayName: z.string().nullable(),
  owner: LeadOwnerSchema,
  property: LeadPropertySchema.nullable(),
  // The lead's live responsible-member set (IMM-344), id + display name, for the
  // „Verantwortliche Mitglieder" display and the „Zuweisen" dialog's pre-selection.
  // Empty on responses that don't resolve it (the detail re-fetches after any
  // mutation).
  responsibleMembers: z.array(z.object({ memberId: refersTo('member', z.uuid()), displayName: z.string() })),
  // Whether the caller may change this lead (IMM-438 write gate): true for a
  // responsible member, an Owner/Workspace-Admin, or a Standortleitung acting
  // within their own Standort subtree. The detail page hides every edit /
  // lifecycle / activity control when false; the backend gate is the boundary.
  // Responses to a successful mutation (and non-detail reads) default to true —
  // only the detail GET resolves the caller's real authority.
  canEdit: z.boolean(),
  // IMM-1447 — when HLRE cancelled this lead (from the HLRE partner lead reference), else null.
  // A non-null value means the lead is read-only for every member: `canEdit` is false and every write returns 422 `lead.lead.cancelled-by-hlre`.
  hlreCancelledAt: z.iso.datetime().nullable(),
  // IMM-569.8 — the lead's own URL plus its activity timeline and, once the lead
  // is qualified, the contact, property and deal it converted into. Those three
  // keys are ABSENT (not null) while the lead is unqualified, because it has
  // converted into nothing. Additive and never load-bearing; a link says the
  // resource exists, never that this key may read it.
  links: LeadLinksSchema,
});

export type LeadResponse = z.infer<typeof LeadResponseSchema>;

// Which dimension(s) a dedup candidate matched on. Defined here because the
// create response reuses it as a warning signal (IMM-26) and the dedicated
// check-duplicates endpoint reuses it below.
export const LeadDuplicateMatchSchema = z.enum(['email', 'address', 'cadastral']);
export type LeadDuplicateDimension = z.infer<typeof LeadDuplicateMatchSchema>;

export const CreateLeadPathParamsSchema = WorkspaceIdPathParamsSchema;
// The create response is the full lead plus the dedup warning and idempotency
// signal (IMM-26). Dedup never blocks: a fresh lead is always returned with
// `duplicateOf` naming the most-recent matching lead (null when none) and
// `duplicateMatch` listing every matched dimension. `idempotentReplay` is true
// when the (api key, partnerLeadId) tuple already produced a lead — the
// returned lead is the pre-existing one and no second insert happened.
export const CreateLeadResponseSchema = LeadResponseSchema.extend({
  duplicateOf: z.uuid().nullable(),
  duplicateMatch: z.array(LeadDuplicateMatchSchema),
  idempotentReplay: z.boolean(),
});
export type CreateLeadResponse = z.infer<typeof CreateLeadResponseSchema>;

export const ListWorkspaceLeadsPathParamsSchema = WorkspaceIdPathParamsSchema;

// Sortable columns the inbox table exposes. `received` / `created` sort on the
// matching timestamp; `name` / `source` / `status` sort on the rendered value.
// `value` sorts on the property's estimated value and `lastContact` on the
// derived last-communication stamp (IMM-419) — both order nulls last in either
// direction so value-less leads always trail.
export const LEAD_SORT_FIELDS = ['received', 'created', 'name', 'source', 'status', 'value', 'lastContact'] as const;
export const LeadSortFieldSchema = z.enum(LEAD_SORT_FIELDS);
export type LeadSortField = z.infer<typeof LeadSortFieldSchema>;

export const LEAD_SORT_DIRECTIONS = ['asc', 'desc'] as const;
export const LeadSortDirectionSchema = z.enum(LEAD_SORT_DIRECTIONS);
export type LeadSortDirection = z.infer<typeof LeadSortDirectionSchema>;

// Comma-separated multi-value filters mirror `IdsFilterQuerySchema`: absent or
// blank normalises to an empty array (= no filter), each populated dimension
// AND-combines with the others. `search` matches name / phone / address
// case-insensitively. `includeArchived` widens the default inbox status set
// (NEW, CONTACTED) to also surface ARCHIVED leads. With no params the endpoint
// keeps the slice-2 inbox behaviour: NEW or CONTACTED, newest-first by createdAt.
export const ListWorkspaceLeadsQuerySchema = z
  .object({
    organizationId: z.uuid().optional(),
    // Narrow to this set of Standorte (a node + its descendants), intersected
    // with the caller's visible set. The frontend computes the subtree id set
    // from the org tree it holds and sends it `?organizationIds=uuid,uuid`. Takes
    // precedence over the single `organizationId` when non-empty; ids outside the
    // visible set are ignored (never widens). Absent / blank → no narrowing.
    organizationIds: commaSeparated(z.uuid()),
    status: commaSeparated(LeadStatusSchema),
    source: commaSeparated(LeadSourceSchema),
    leadPartnerId: commaSeparated(z.uuid()),
    search: z.string().trim().max(120).optional(),
    // The ⌘K palette's free-text filter (IMM-448.4) — deliberately NOT `search`
    // above. `search` is the inbox's plain substring match over name / phone /
    // address; `q` runs the palette's own predicate (name, e-mail, both phone
    // numbers and the Quelle token, diacritic-folded), so „Alle Treffer in Leads"
    // lands on exactly the rows the palette counted. It also lifts the inbox's
    // default NEW/CONTACTED status narrowing, because the palette counts every
    // lead the member may open. Absent or blank → no search.
    q: ListSearchTermSchema,
    sortField: LeadSortFieldSchema.optional(),
    sortDirection: LeadSortDirectionSchema.optional(),
    includeArchived: z.stringbool().optional(),
    // „Mir zugewiesen" (IMM-344.4): narrow to leads whose current responsible set
    // includes the caller. The member id is resolved server-side from the request
    // context — never sent by the client — so this is a bare boolean.
    assignedToMe: z.stringbool().optional(),
    // „Verwaiste Leads" (IMM-344.5): narrow to leads with an empty responsible set
    // (orphaned by a member deactivation). Admin-only — the list route rejects it
    // without `organization:lead:lead-assign`.
    orphaned: z.stringbool().optional(),
    // Narrow to leads whose current responsible set contains any of these members (dashboard Team table link).
    // ANDed over the caller's visibility, so it narrows only. Absent → `[]` → no narrowing.
    responsibleMemberIds: commaSeparated(z.uuid()),
    // Intake window `[receivedFrom, receivedTo)` (dashboard drill-down links).
    receivedFrom: z.iso.datetime().optional(),
    receivedTo: z.iso.datetime().optional(),
  })
  .refine(
    (query) =>
      query.receivedFrom === undefined ||
      query.receivedTo === undefined ||
      Date.parse(query.receivedFrom) < Date.parse(query.receivedTo),
    { path: ['receivedTo'], message: 'receivedTo must be after receivedFrom' },
  );
export type ListWorkspaceLeadsQuery = z.infer<typeof ListWorkspaceLeadsQuerySchema>;

export const ListWorkspaceLeadsResponseSchema = z.object({ data: z.array(LeadListItemSchema) });
export type ListWorkspaceLeadsResponse = z.infer<typeof ListWorkspaceLeadsResponseSchema>;

export const GetWorkspaceLeadPathParamsSchema = z.object({ workspaceId: z.uuid(), id: z.uuid() });
export const GetWorkspaceLeadResponseSchema = LeadResponseSchema;

// Dedup is a warning surfaced at capture time, never a block. The form
// calls this before saving; an empty `duplicates` array means no match.
export const CheckLeadDuplicatesPathParamsSchema = WorkspaceIdPathParamsSchema;
export const CheckLeadDuplicatesBodySchema = z
  .object({
    email: z.email().optional(),
    street: z.string().trim().min(1).optional(),
    postalCode: z.string().trim().min(1).optional(),
    cadastralDistrict: z.string().trim().min(1).optional(),
    cadastralParcel: z.string().trim().min(1).optional(),
  })
  .strict();
export type CheckLeadDuplicatesBody = z.infer<typeof CheckLeadDuplicatesBodySchema>;

export const LeadDuplicateSchema = z.object({
  id: z.uuid(),
  displayName: z.string(),
  matchedOn: z.array(LeadDuplicateMatchSchema).min(1),
});
export type LeadDuplicate = z.infer<typeof LeadDuplicateSchema>;
export const CheckLeadDuplicatesResponseSchema = z.object({ duplicates: z.array(LeadDuplicateSchema) });
export type CheckLeadDuplicatesResponse = z.infer<typeof CheckLeadDuplicatesResponseSchema>;

// Lifecycle transitions (IMM-192). All scoped to a single lead by workspace + id.
const LeadActionPathParamsSchema = z.object({ workspaceId: z.uuid(), id: z.uuid() });

export const MarkLeadLostPathParamsSchema = LeadActionPathParamsSchema;
export const MarkLeadLostBodySchema = z
  .object({
    lossReason: LeadLossReasonSchema,
    lossNote: z.string().trim().max(LEAD_LOSS_NOTE_MAX_LENGTH).optional(),
  })
  .strict();
export type MarkLeadLostBody = z.infer<typeof MarkLeadLostBodySchema>;
export const MarkLeadLostResponseSchema = LeadResponseSchema;

export const ArchiveLeadPathParamsSchema = LeadActionPathParamsSchema;
export const ArchiveLeadBodySchema = z.object({}).strict();
export const ArchiveLeadResponseSchema = LeadResponseSchema;

export const ReopenLeadPathParamsSchema = LeadActionPathParamsSchema;
export const ReopenLeadBodySchema = z.object({}).strict();
export const ReopenLeadResponseSchema = LeadResponseSchema;

// Qualify an Owner-Lead (IMM-213 / IMM-329). The body carries the chosen
// `organizationId` (Standort) the caller picks at qualify time — pre-seeded on
// the frontend with the lead's current org. That org scopes the Contact,
// Property and DRAFT Deal created in the same atomic transaction (their owner +
// property fields still come from the lead's sub-records) and is persisted on
// the lead. The response is the now-CONVERTED lead carrying the created ids.
export const QualifyLeadPathParamsSchema = LeadActionPathParamsSchema;

// The two phases a freshly qualified lead may start its Deal in (IMM-401):
// right after a first exchange (`IN_EXCHANGE`) or once a viewing is booked
// (`APPOINTMENT_SCHEDULED`). Restricting the enum here is defence-in-depth — the
// deal domain (`Deal.build`, `ALLOWED_INITIAL_PHASES`) is the enforcing
// boundary and must be kept in step with this list. The tuple is asserted
// against `DealPhase` so a value can never fall outside the phase set.
export const QUALIFY_INITIAL_PHASES = ['IN_EXCHANGE', 'APPOINTMENT_SCHEDULED'] as const satisfies readonly DealPhase[];
export const QualifyInitialPhaseSchema = z.enum(QUALIFY_INITIAL_PHASES);
export type QualifyInitialPhase = z.infer<typeof QualifyInitialPhaseSchema>;

// Qualifying stages a Property (in IN_ACQUISITION) + an active Deal from the
// lead. `initialPhase` picks which of the two start phases the Deal is born in
// (default `IN_EXCHANGE`); `note` is the optional qualification reason, stored
// on the Deal's initial phase-history row (IMM-401). The property is created
// without a price — pricing is owned by the AVM baseline (IMM-247/248), so no
// `purchasePrice` is accepted here. Address + area come from the lead.
// `marketingType` (IMM-792.2) is optional here because whether it is required
// depends on the lead being qualified (it falls back to `lead.intent`), which
// this schema cannot see — the handler enforces the presence rule.
// `ownerFirstName` (IMM-989.1) follows the same rule: a Contact person needs a
// first name, the lead may record none, so the handler asks when neither exists.
export const QualifyLeadBodySchema = z
  .object({
    organizationId: z.uuid(),
    initialPhase: QualifyInitialPhaseSchema.default('IN_EXCHANGE'),
    note: z.string().trim().max(1000).optional(),
    marketingType: TransactionTypeSchema.optional(),
    ownerFirstName: z.string().trim().min(1).max(80).optional(),
  })
  .strict();
export type QualifyLeadBody = z.infer<typeof QualifyLeadBodySchema>;
export const QualifyLeadResponseSchema = LeadResponseSchema;

// Owner-section edit (lead-edit, IMM-227). Replace-wholesale: the body is the
// full owner shape (same as capture), overwriting the lead_owner row. Email /
// phone are validated + normalised at the presenter (400 on bad input).
export const UpdateLeadOwnerPathParamsSchema = LeadActionPathParamsSchema;
export const UpdateLeadOwnerBodySchema = CreateLeadOwnerSchema;
export type UpdateLeadOwnerBody = z.infer<typeof UpdateLeadOwnerBodySchema>;
export const UpdateLeadOwnerResponseSchema = LeadResponseSchema;

// Notes-section edit (lead-edit, IMM-230). The notes column is free text;
// blank/absent clears it to null (via `blankToNull` in the domain).
export const UpdateLeadNotesPathParamsSchema = LeadActionPathParamsSchema;
export const UpdateLeadNotesBodySchema = z.object({ notes: z.string().trim().max(5000).optional() }).strict();
export type UpdateLeadNotesBody = z.infer<typeof UpdateLeadNotesBodySchema>;
export const UpdateLeadNotesResponseSchema = LeadResponseSchema;

// Legal-basis edit (lead-edit, IMM-231). Updates the core `leads.legal_basis`
// column; the body carries the required GDPR legal basis enum.
export const UpdateLeadLegalBasisPathParamsSchema = LeadActionPathParamsSchema;
export const UpdateLeadLegalBasisBodySchema = z.object({ legalBasis: LegalBasisSchema }).strict();
export type UpdateLeadLegalBasisBody = z.infer<typeof UpdateLeadLegalBasisBodySchema>;
export const UpdateLeadLegalBasisResponseSchema = LeadResponseSchema;

// Intent edit (lead-edit, IMM-792.1). Updates the core `leads.intent` column —
// the owner's Absicht (SALE / RENTAL). `null` clears it back to "not yet
// asked". Refused once the lead is qualified, via the same lead-mutation guard
// every other section edit uses.
export const UpdateLeadIntentPathParamsSchema = LeadActionPathParamsSchema;
export const UpdateLeadIntentBodySchema = z.object({ intent: TransactionTypeSchema.nullable() }).strict();
export type UpdateLeadIntentBody = z.infer<typeof UpdateLeadIntentBodySchema>;
export const UpdateLeadIntentResponseSchema = LeadResponseSchema;

// Address-section edit (lead-edit, IMM-229). Updates only the location/address
// columns of the shared `lead_property` sub-record; the object-characteristic
// columns (object type, areas, value) are preserved untouched. Mirrors the
// capture address step's fields + validation.
export const UpdateLeadPropertyAddressPathParamsSchema = LeadActionPathParamsSchema;
export const UpdateLeadPropertyAddressBodySchema = z
  .object({
    street: z.string().trim().max(120).optional(),
    houseNumber: z.string().trim().max(10).optional(),
    postalCode: z.string().trim().max(20).optional(),
    city: z.string().trim().max(80).optional(),
    country: z.string().trim().max(80).default('Deutschland'),
    cadastralDistrict: z.string().trim().max(80).optional(),
    cadastralParcel: z.string().trim().max(40).optional(),
    latitude: z.number().min(-90).max(90).optional(),
    longitude: z.number().min(-180).max(180).optional(),
  })
  .strict();
export type UpdateLeadPropertyAddressBody = z.infer<typeof UpdateLeadPropertyAddressBodySchema>;
export const UpdateLeadPropertyAddressResponseSchema = LeadResponseSchema;

// Property-section edit (lead-edit, IMM-228). Object characteristics only — the
// address columns of the shared lead_property row are owned by the Address
// section and preserved by this endpoint. Replace-wholesale for these fields.
export const UpdateLeadPropertyPathParamsSchema = LeadActionPathParamsSchema;
export const UpdateLeadPropertyBodySchema = z
  .object({
    category: PropertyCategorySchema.optional(),
    subtype: PropertySubtypeSchema.optional(),
    constructionYear: z.number().int().min(1700).max(2100).optional(),
    livingArea: z.number().min(0).optional(),
    plotArea: z.number().min(0).optional(),
    estimatedValue: z.number().min(0).optional(),
  })
  .strict()
  .superRefine(refineImmobilientyp);
export type UpdateLeadPropertyBody = z.infer<typeof UpdateLeadPropertyBodySchema>;
export const UpdateLeadPropertyResponseSchema = LeadResponseSchema;

// --- Lead activity write path (IMM-259) -------------------------------------
// A lead gets the same manual activity capture the Contact / Property / Deal
// surfaces have, but recorded against the lead itself (a lead is not a contact).
// Each note / communication is the source of truth in `lead_notes` /
// `lead_communications`; the activity timeline projects it from the emitted
// `lead.note-recorded` / `lead.communication-recorded` event onto a LEAD-linked,
// workspace-scoped activity. The taxonomy is intentionally aligned with the
// Contact one (IMM-10) but kept lead-local so the lead contract stays decoupled
// from the relationship contract.

export const LEAD_NOTE_MAX_LENGTH = 10_000;

// Plannable lead activities (IMM-418.1). An activity's persisted truth is its
// `state`: `DONE` (logged, has an `occurredAt` ≤ now — the pre-418 behaviour),
// `OPEN` (planned, has a future `dueAt`, no `occurredAt`) or `CANCELLED`
// (reachable only from IMM-418.4). „Überfällig" is derived read-side
// (`state = OPEN AND dueAt < now`) and never persisted or put on the wire.
export const LEAD_ACTIVITY_STATES = ['OPEN', 'DONE', 'CANCELLED'] as const;
export const LeadActivityStateSchema = z.enum(LEAD_ACTIVITY_STATES);
export type LeadActivityState = z.infer<typeof LeadActivityStateSchema>;

// Request-only discriminator: `LOG` records what already happened (→ DONE),
// `PLAN` schedules a Wiedervorlage (→ OPEN). Never persisted — the persisted
// truth is `state`. Absent defaults to `LOG` so pre-418 callers keep logging.
export const LEAD_ACTIVITY_TIME_MODES = ['LOG', 'PLAN'] as const;
export const LeadActivityTimeModeSchema = z.enum(LEAD_ACTIVITY_TIME_MODES);
export type LeadActivityTimeMode = z.infer<typeof LeadActivityTimeModeSchema>;

// The Ergebnis of a completed communication (IMM-418.2): whether the owner was
// actually reached and, if not, why. Only meaningful on a DONE communication —
// a Notiz never carries one, and it is null on a communication logged before
// this slice. English literals on the wire; German labels render via @immo/i18n.
export const LEAD_ACTIVITY_OUTCOMES = COMMUNICATION_OUTCOME_TOKENS;
export const LeadActivityOutcomeSchema = z.enum(LEAD_ACTIVITY_OUTCOMES);
export type LeadActivityOutcome = z.infer<typeof LeadActivityOutcomeSchema>;

// `occurredAt` and `dueAt` are mutually exclusive and mode-driven: LOG carries
// an optional past `occurredAt` (never a `dueAt`); PLAN requires a `dueAt` and
// forbids `occurredAt`. The future-date guard on `dueAt` (and the ≤ now guard on
// `occurredAt`) live in the domain — this refine only enforces presence/exclusion.
function refinePlannableActivity(
  value: {
    readonly mode?: LeadActivityTimeMode | undefined;
    readonly occurredAt?: string | undefined;
    readonly dueAt?: string | undefined;
  },
  ctx: z.RefinementCtx,
): void {
  const mode = value.mode ?? 'LOG';

  if (mode === 'PLAN') {
    if (value.dueAt === undefined) {
      ctx.addIssue({ code: 'custom', path: ['dueAt'], message: 'dueAt is required when mode is PLAN' });
    }

    if (value.occurredAt !== undefined) {
      ctx.addIssue({ code: 'custom', path: ['occurredAt'], message: 'occurredAt is forbidden when mode is PLAN' });
    }

    return;
  }

  if (value.dueAt !== undefined) {
    ctx.addIssue({ code: 'custom', path: ['dueAt'], message: 'dueAt is forbidden when mode is LOG' });
  }
}

// Record (LOG) or plan (PLAN) a note against a lead. `occurredAt` defaults to now
// server-side; the future-date guard (≤ now, 1-minute skew) lives in the domain.
// `assigneeMemberId` (Zuständiger) defaults to the acting member and must be an
// assignable workspace member; `dueAt` is required + future for PLAN.
export const RecordLeadNotePathParamsSchema = LeadActionPathParamsSchema;
export const RecordLeadNoteBodySchema = z
  .object({
    body: z.string().min(1).max(LEAD_NOTE_MAX_LENGTH),
    mode: LeadActivityTimeModeSchema.optional(),
    occurredAt: z.iso.datetime().optional(),
    dueAt: z.iso.datetime().optional(),
    assigneeMemberId: z.uuid().optional(),
  })
  .strict()
  .superRefine(refinePlannableActivity);
export type RecordLeadNoteBody = z.infer<typeof RecordLeadNoteBodySchema>;
export const LeadNoteResponseSchema = z.object({
  id: z.uuid(),
  body: z.string(),
  createdByActorId: refersTo('member', z.uuid()).nullable(),
  state: LeadActivityStateSchema,
  // Null for a planned (OPEN) note — it has not occurred yet.
  occurredAt: z.iso.datetime().nullable(),
  // Set for a planned (OPEN) note (its „Fällig am"); null for a logged one.
  dueAt: z.iso.datetime().nullable(),
  assigneeMemberId: refersTo('member', z.uuid()).nullable(),
  createdAt: z.iso.datetime(),
});
export type LeadNoteResponse = z.infer<typeof LeadNoteResponseSchema>;
export const RecordLeadNoteResponseSchema = LeadNoteResponseSchema;
export type RecordLeadNoteResponse = z.infer<typeof RecordLeadNoteResponseSchema>;

// A communication is the lead reaching out or being reached — distinct from a
// Note (an internal observation). Each carries a direction; Fax/Letter/E-Mail
// also a subject, Call also a duration.
export const LEAD_COMMUNICATION_TYPES = MANUAL_COMMUNICATION_TYPES;
export const LeadCommunicationTypeSchema = z.enum(LEAD_COMMUNICATION_TYPES);
export type LeadCommunicationType = z.infer<typeof LeadCommunicationTypeSchema>;

export const LEAD_COMMUNICATION_DIRECTIONS = ['INBOUND', 'OUTBOUND'] as const;
export const LeadCommunicationDirectionSchema = z.enum(LEAD_COMMUNICATION_DIRECTIONS);
export type LeadCommunicationDirection = z.infer<typeof LeadCommunicationDirectionSchema>;

export const LEAD_COMMUNICATION_CONTENT_MAX_LENGTH = 10_000;
export const LEAD_COMMUNICATION_SUBJECT_MAX_LENGTH = 200;
export const LEAD_COMMUNICATION_DURATION_MAX_MINUTES = 1_440;

// `subject` is only meaningful for FAX / LETTER / EMAIL and `durationMinutes`
// only for CALL; the domain ignores them for other types. `occurredAt`
// defaults to now; the future-date guard (≤ now, 1-minute skew) lives in the
// domain.
export const RecordLeadCommunicationPathParamsSchema = LeadActionPathParamsSchema;
export const RecordLeadCommunicationBodySchema = z
  .object({
    type: LeadCommunicationTypeSchema,
    direction: LeadCommunicationDirectionSchema,
    content: z.string().min(1).max(LEAD_COMMUNICATION_CONTENT_MAX_LENGTH),
    subject: z.string().max(LEAD_COMMUNICATION_SUBJECT_MAX_LENGTH).optional(),
    durationMinutes: z.number().int().min(0).max(LEAD_COMMUNICATION_DURATION_MAX_MINUTES).optional(),
    mode: LeadActivityTimeModeSchema.optional(),
    occurredAt: z.iso.datetime().optional(),
    dueAt: z.iso.datetime().optional(),
    assigneeMemberId: z.uuid().optional(),
    // „Jetzt festhalten" Ergebnis (IMM-418.2): a DONE communication may carry an
    // outcome at creation. Forbidden for PLAN — an outcome only exists on a
    // completed activity, never a planned one.
    outcome: LeadActivityOutcomeSchema.optional(),
  })
  .strict()
  .superRefine(refinePlannableActivity)
  .superRefine((value, ctx) => {
    if ((value.mode ?? 'LOG') === 'PLAN' && value.outcome !== undefined) {
      ctx.addIssue({ code: 'custom', path: ['outcome'], message: 'outcome is forbidden when mode is PLAN' });
    }
  });
export type RecordLeadCommunicationBody = z.infer<typeof RecordLeadCommunicationBodySchema>;
export const LeadCommunicationResponseSchema = z.object({
  id: z.uuid(),
  type: LeadCommunicationTypeSchema,
  direction: LeadCommunicationDirectionSchema,
  content: z.string(),
  subject: z.string().nullable(),
  durationMinutes: z.number().int().nullable(),
  createdByActorId: refersTo('member', z.uuid()).nullable(),
  state: LeadActivityStateSchema,
  // Null for a planned (OPEN) communication — it has not occurred yet.
  occurredAt: z.iso.datetime().nullable(),
  // Set for a planned (OPEN) communication (its „Fällig am"); null for a logged one.
  dueAt: z.iso.datetime().nullable(),
  assigneeMemberId: refersTo('member', z.uuid()).nullable(),
  // The Ergebnis of a completed communication (IMM-418.2); null for a planned
  // (OPEN) one, or a DONE one logged without an outcome (pre-slice rows).
  outcome: LeadActivityOutcomeSchema.nullable(),
  createdAt: z.iso.datetime(),
});
export type LeadCommunicationResponse = z.infer<typeof LeadCommunicationResponseSchema>;
export const RecordLeadCommunicationResponseSchema = LeadCommunicationResponseSchema;
export type RecordLeadCommunicationResponse = z.infer<typeof RecordLeadCommunicationResponseSchema>;

// --- Lead activity completion (IMM-418.2) -----------------------------------
// Complete an OPEN (planned) note / communication: transition it to DONE,
// capturing „Stattgefunden am" (occurredAt, ≤ now + 60s skew, default now), an
// optional completion note appended to the content, and — for a communication —
// an Ergebnis. The activity is addressed by its UUID (unique across both tables,
// Decision D9); the handler resolves which table it lives in. First lead-activity
// UPDATE path (lead activities were append-only before this slice).
export const CompleteLeadActivityPathParamsSchema = z.object({
  workspaceId: z.uuid(),
  id: z.uuid(),
  activityId: z.uuid(),
});
export type CompleteLeadActivityPathParams = z.infer<typeof CompleteLeadActivityPathParamsSchema>;

export const CompleteLeadActivityBodySchema = z
  .object({
    occurredAt: z.iso.datetime().optional(),
    outcome: LeadActivityOutcomeSchema.optional(),
    note: z.string().trim().max(LEAD_COMMUNICATION_CONTENT_MAX_LENGTH).optional(),
    // Optional override of the auto-follow-up's default „Fällig am" (IMM-418.3).
    // Applies only to a non-„Erreicht" communication completion, which always
    // spawns a follow-up; the server defaults it to occurredAt + 24h when absent.
    // Rejected on a „Erreicht" completion or a note (no follow-up applies) and
    // rejected when in the past — both guarded in the domain.
    followUpDueAt: z.iso.datetime().optional(),
  })
  .strict();
export type CompleteLeadActivityBody = z.infer<typeof CompleteLeadActivityBodySchema>;

// The completed activity may be a note or a communication (`activityKind`
// discriminates). `outcome` is always null for a NOTE; `content` carries the
// note-appended content so the caller can refresh the timeline entry in place.
export const LEAD_ACTIVITY_KINDS = ['NOTE', 'COMMUNICATION'] as const;
export const LeadActivityKindSchema = z.enum(LEAD_ACTIVITY_KINDS);
export type LeadActivityKind = z.infer<typeof LeadActivityKindSchema>;

export const CompleteLeadActivityResponseSchema = z.object({
  id: z.uuid(),
  activityKind: LeadActivityKindSchema,
  state: LeadActivityStateSchema,
  occurredAt: z.iso.datetime(),
  outcome: LeadActivityOutcomeSchema.nullable(),
  content: z.string(),
  // The auto-created Wiedervorlage (IMM-418.3) for a non-„Erreicht" communication
  // completion — its new activity id and „Fällig am" — or null when none was
  // created („Erreicht", or a note).
  followUp: z
    .object({
      activityId: z.uuid(),
      dueAt: z.iso.datetime(),
    })
    .nullable(),
});
export type CompleteLeadActivityResponse = z.infer<typeof CompleteLeadActivityResponseSchema>;

// The three open-activity transitions of IMM-418.4 — reschedule, cancel,
// reassign — each address an activity by its UUID, like completion (Decision D9).
export const LeadActivityTransitionPathParamsSchema = z.object({
  workspaceId: z.uuid(),
  id: z.uuid(),
  activityId: z.uuid(),
});
export type LeadActivityTransitionPathParams = z.infer<typeof LeadActivityTransitionPathParamsSchema>;

// The updated activity returned by every open-activity transition (IMM-418.4).
// `dueAt` is non-null for a still-open (rescheduled) activity; `state` becomes
// CANCELLED on cancel; `assigneeMemberId` carries the current Zuständiger.
export const LeadActivityTransitionResponseSchema = z.object({
  id: z.uuid(),
  activityKind: LeadActivityKindSchema,
  state: LeadActivityStateSchema,
  dueAt: z.iso.datetime().nullable(),
  occurredAt: z.iso.datetime().nullable(),
  outcome: LeadActivityOutcomeSchema.nullable(),
  assigneeMemberId: refersTo('member', z.uuid()).nullable(),
  content: z.string(),
});
export type LeadActivityTransitionResponse = z.infer<typeof LeadActivityTransitionResponseSchema>;

// Reschedule (IMM-418.4): replace „Fällig am" with a required future date. The
// activity stays OPEN; the prior due date is retained in the audited event.
export const RescheduleLeadActivityBodySchema = z
  .object({
    dueAt: z.iso.datetime(),
  })
  .strict();
export type RescheduleLeadActivityBody = z.infer<typeof RescheduleLeadActivityBodySchema>;

// Cancel (IMM-418.4): close an open activity as CANCELLED — no body, no reason.
export const CancelLeadActivityBodySchema = z.object({}).strict();
export type CancelLeadActivityBody = z.infer<typeof CancelLeadActivityBodySchema>;

// Reassign (IMM-418.4): move an open activity to another assignable member.
export const ReassignLeadActivityBodySchema = z
  .object({
    assigneeMemberId: z.uuid(),
  })
  .strict();
export type ReassignLeadActivityBody = z.infer<typeof ReassignLeadActivityBodySchema>;

// „Meine Aufgaben" (IMM-418.5) — the cross-lead feed of the acting member's open
// (planned) activities, overdue first. A pure read over the lead module: open
// activities ARE the tasks, so there is no task entity, no write path. The view
// inherits the lead-list visibility scoping and excludes activities on terminal
// leads (Decision D5) — only NEW / CONTACTED leads contribute.
export const ListMyTasksPathParamsSchema = WorkspaceIdPathParamsSchema;
export const DEFAULT_MY_TASKS_PAGE_SIZE = 50;
export const ListMyTasksQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).optional(),
  // Opaque keyset cursor from a previous page's `nextCursor`.
  cursor: z.string().optional(),
});
export type ListMyTasksQuery = z.infer<typeof ListMyTasksQuerySchema>;

// One row of „Meine Aufgaben". `type` is the communication type (CALL / SMS / …)
// and is null for a NOTE; `overdue` is derived read-side (`dueAt < now`) and
// never stored. `leadName` is the owner's display name, so the row can link back
// to — and label — the lead it belongs to.
export const MyTaskItemSchema = z.object({
  activityId: z.uuid(),
  kind: LeadActivityKindSchema,
  type: LeadCommunicationTypeSchema.nullable(),
  // Travels with `type` and is null exactly when it is — `lead_communications`
  // carries a NOT NULL direction and the NOTE branch selects NULL, so the pair
  // moves together. „Meine Aufgaben" renders „{Typ} · {Richtung}" from it
  // (IMM-639).
  direction: LeadCommunicationDirectionSchema.nullable(),
  // The activity's own text — `lead_notes.body` / `lead_communications.content`,
  // both NOT NULL. Carried because the row is otherwise unidentifiable: a title
  // of „Notiz" or „Anruf · Ausgehend" says what kind of thing is due, never what
  // it is about (IMM-753). The deal half has always carried its equivalent.
  content: z.string(),
  dueAt: z.iso.datetime(),
  assigneeMemberId: refersTo('member', z.uuid()),
  leadId: refersTo('lead', z.uuid()),
  leadName: z.string(),
  overdue: z.boolean(),
});
export type MyTaskItem = z.infer<typeof MyTaskItemSchema>;

export const ListMyTasksResponseSchema = z.object({
  items: z.array(MyTaskItemSchema),
  // Pass back as `?cursor=` for the next page; null when the feed is exhausted.
  nextCursor: z.string().nullable(),
});
export type ListMyTasksResponse = z.infer<typeof ListMyTasksResponseSchema>;

// Cross-Standort reassignment (IMM-20.4 AC22). Workspace-addressed:
// `POST /v1/workspaces/:workspaceId/leads/:id/standort`. The body carries the
// target organisation (note: the lead column is `organisationId`); the handler's
// authz rule decides whether the move is allowed.
export const ReassignLeadStandortPathParamsSchema = z.object({
  workspaceId: z.uuid(),
  id: z.uuid(),
});
export type ReassignLeadStandortPathParams = z.infer<typeof ReassignLeadStandortPathParamsSchema>;

export const ReassignLeadStandortBodySchema = z
  .object({
    organizationId: z.uuid(),
  })
  .strict();
export type ReassignLeadStandortBody = z.infer<typeof ReassignLeadStandortBodySchema>;

export const ReassignLeadStandortResponseSchema = LeadResponseSchema;
export type ReassignLeadStandortResponse = z.infer<typeof ReassignLeadStandortResponseSchema>;

// Manual reassignment of a lead's responsible members (IMM-344.3). The set
// replaces the previous one; at least one member is required.
export const AssignLeadResponsibleMembersPathParamsSchema = LeadActionPathParamsSchema;
export const AssignLeadResponsibleMembersBodySchema = z.object({
  memberIds: z.array(z.uuid()).min(1),
});
export type AssignLeadResponsibleMembersBody = z.infer<typeof AssignLeadResponsibleMembersBodySchema>;
export const AssignLeadResponsibleMembersResponseSchema = LeadResponseSchema;
export type AssignLeadResponsibleMembersResponse = z.infer<typeof AssignLeadResponsibleMembersResponseSchema>;

// The members the caller may assign a lead to (IMM-344.3): every workspace member
// for Owner/Workspace-Admin; only the caller's Standort subtree for a
// Standortleitung. Scoped server-side off the caller's visible organisations.
export const ListAssignableMembersPathParamsSchema = WorkspaceIdPathParamsSchema;
export const AssignableMemberSchema = z.object({
  memberId: refersTo('member', z.uuid()),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  email: z.string(),
  // The Standorte this member belongs to — the client groups the assignment
  // picker by the lead's own Standort (IMM-485). The handler already reads this
  // to filter on; it simply was not mapped through.
  organizationIds: z.array(refersTo('organization', z.uuid())),
});
export type AssignableMember = z.infer<typeof AssignableMemberSchema>;
export const ListAssignableMembersResponseSchema = z.object({ data: z.array(AssignableMemberSchema) });
export type ListAssignableMembersResponse = z.infer<typeof ListAssignableMembersResponseSchema>;
